import { LIST_TYPE_QUESTION } from 'constants';
import { LIST_CONTROL_IMAGE } from 'constants';

import { useEffect, useRef, useState } from 'react';

import moment from 'moment';

import Input from 'components/Input';
import { ENUM_ACTIVE_CHECKBOX, ENUM_IMAGE_LAYOUT, ENUM_QUESTION_TYPE } from 'constants/enum';
import { TEXT_CREATE_SURVEY } from 'constants/string/texts';
import { useClickOutside } from 'hooks/useClickOutside';
import useCreateSurvey from 'hooks/useCreateSurvey';
import { base64ToFile, fileToBase64, getImageDimensionsFromFile, resizeImage } from 'utils/helper';

const AddPhoto = require('assets/resources/AddPhoto.svg').default;
const MoreVert = require('assets/resources/MoreVert.svg').default;

const QuestionOption = (props) => {
  const { onSaveStep } = useCreateSurvey();
  const { data, form, sectionIndex, questionIndex, optionals } = props;

  const imageRef = useRef(null);
  const controlImageRef = useRef(null);
  const [showControlImage, setShowControlImage] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const dimensions = await getImageDimensionsFromFile(file);
      const h_div_w = dimensions.height / dimensions.width;
      const imageApplyNewSize = await resizeImage(file, 200, 200 * h_div_w);
      const base64String = await fileToBase64(imageApplyNewSize);
      const images = [
        {
          url_preview: URL.createObjectURL(file),
          image_url: base64String,
          size: { width: 200, height: 200 * h_div_w },
          image_layout: ENUM_IMAGE_LAYOUT.LEFT,
        },
      ];
      form.setFieldValue(['sections', sectionIndex, 'questions', questionIndex, 'images'], images);
      const question = form.values.sections[sectionIndex].questions[questionIndex];
      question.images = images;
      onSaveStep(form.values);
    }
  };

  const startResize = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (isResizing) {
      const images = form?.values.sections?.[sectionIndex]?.questions?.[questionIndex]?.images?.[0];
      let boundingClientRect = imageRef.current.getBoundingClientRect().left;
      let newWidth = e.clientX - boundingClientRect;

      if (images.image_layout === ENUM_IMAGE_LAYOUT.RIGHT) {
        boundingClientRect = imageRef.current.getBoundingClientRect().right;
        newWidth = boundingClientRect - e.clientX;
      }

      const newHeight = (newWidth * (images.size.height || NaN)) / images.size.width;
      const newSize = { width: newWidth, height: newHeight };
      form.setFieldValue(['sections', sectionIndex, 'questions', questionIndex, 'images', 0, 'size'], newSize);
    }
  };

  const stopResize = async () => {
    if (imageRef && imageRef.current && isResizing) {
      const currentImage = await base64ToFile(data?.images[0]?.image_url, moment().unix());
      const imageApplyNewSize = await resizeImage(currentImage, imageRef?.current?.clientWidth, imageRef?.current?.clientHeight);
      const base64String = await fileToBase64(imageApplyNewSize);
      form.setFieldValue(['sections', sectionIndex, 'questions', questionIndex, 'images', 0, 'image_url'], base64String);
      const question = form.values.sections[sectionIndex].questions[questionIndex].images[0];
      question.image_url = base64String;
      onSaveStep(form.values);
    }

    setIsResizing(false);
  };

  const handleChangeLayoutImage = (layout) => {
    if (layout === 'delete') {
      form.setFieldValue(['sections', sectionIndex, 'questions', questionIndex, 'images'], []);
      const question = form.values.sections[sectionIndex].questions[questionIndex];
      question.images = [];
    } else {
      form.setFieldValue(['sections', sectionIndex, 'questions', questionIndex, 'images', 0, 'image_layout'], layout);
    }

    onSaveStep(form.values);
  };

  useClickOutside(controlImageRef, () => setShowControlImage(false));

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResize);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', stopResize);
    };
  }, [isResizing]);

  const handleChangeQuestionType = (value) => {
    form.setFieldValue(['sections', sectionIndex, 'questions', questionIndex, 'question_type'], value);
    const question = form.values.sections[sectionIndex].questions[questionIndex];
    question.question_type = value;
    onSaveStep(form.values);
  };

  return (
    <>
      {/* Config question */}
      <div className="flex items-center justify-between gap-2 relative mb-4 pt-4">
        <Input
          type="text"
          value={data?.title}
          placeholder={TEXT_CREATE_SURVEY.PLACEHOLDER_ENTER_THE_QUESTION}
          className="!max-w-113.75 !w-full !h-12"
          classNameInput="!rounded-lg !max-w-113.75 !w-full !h-12 text-gray-102/65"
          name={`sections[${sectionIndex}].questions[${questionIndex}].title`}
          onChange={form.handleChange}
          onBlur={() => onSaveStep(form.values)}
        />

        <label htmlFor="add-photo">
          <img className="min-w-6 min-h-6 cursor-pointer" src={AddPhoto} alt="add photo" width={24} height={24} />
          <Input
            value=""
            onChange={handleImageChange}
            type="file"
            accept={'image/png, image/jpeg'}
            id="add-photo"
            className="!w-0 !h-0 p-0 hidden"
            classNameInput="!w-0 !h-0 p-0"
          />
        </label>

        <Input
          type="dropdown"
          value={data?.question_type || ENUM_QUESTION_TYPE.SELECT_QUESTION}
          list={LIST_TYPE_QUESTION}
          placeholder={TEXT_CREATE_SURVEY.PLACEHOLDER_SELECT_QUESTION}
          className="!h-12 !max-w-113.75 !w-full type-question-select"
          classNameInput="rounded-lg !h-12 !max-w-113.75 !w-full !text-black-102 !font-normal"
          classNameInputOption="rounded-lg !h-8 text-black opacity-85 !font-normal"
          popupClassName="select-question-type"
          onChange={handleChangeQuestionType}
        />
      </div>

      {/* Type question description */}
      {data?.active_checkbox && data?.active_checkbox?.indexOf(ENUM_ACTIVE_CHECKBOX.EXPLANATION) !== -1 && (
        <Input
          value={data?.description}
          onChange={form.handleChange}
          placeholder={TEXT_CREATE_SURVEY.PLACEHOLDER_ENTER_DESCRIPTION_QUESTION}
          className="w-full mb-3"
          classNameInput="w-full border-0 bg-transparent focus:border-b border-dashed !border-gray-102/65 text-gray-102/65 !p-0"
          name={`sections[${sectionIndex}].questions[${questionIndex}].description`}
          onBlur={() => onSaveStep(form.values)}
        />
      )}

      {/* Image Preview */}
      {(() => {
        const imagePreview = data?.images?.[0];

        if (imagePreview) {
          return (
            <div
              className={`w-full resize-${imagePreview?.image_layout === ENUM_IMAGE_LAYOUT.RIGHT ? 'left' : 'right'}`}
              style={{ display: 'flex', justifyContent: imagePreview?.image_layout ?? ENUM_IMAGE_LAYOUT.LEFT }}
            >
              <div className="image-preview-container" ref={imageRef} tabIndex={0}>
                <div
                  ref={controlImageRef}
                  onClick={() => setShowControlImage(!showControlImage)}
                  className="control-image w-8 h-8 rounded-full border border-solid border-gray-651 bg-white flex justify-center items-center -top-4 -left-4 absolute z-2"
                >
                  {/* Control format image [left - right - center - delete] */}
                  <img src={MoreVert} alt="icon more vert" width={18} height={18} />
                  {showControlImage && (
                    <div className="absolute w-30 h-37 py-1 bg-white shadow-soft-md rounded-sm left-full top-full">
                      {LIST_CONTROL_IMAGE.map((item, index) => (
                        <div
                          onClick={() => {
                            handleChangeLayoutImage(item.value);
                          }}
                          key={`control-image-${index}`}
                          className="flex items-center gap-2 px-3 h-8.75 hover:bg-teal-732 cursor-pointer"
                        >
                          {item.icon}
                          <span className="text-sm">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Image preview */}
                <img
                  src={imagePreview?.url_preview}
                  alt="Image Preview"
                  className="image-preview"
                  style={{ width: imagePreview?.size?.width, height: imagePreview?.size?.height }}
                />
                <div className="resize-handle" onMouseDown={startResize} />
              </div>
            </div>
          );
        } else {
          return null;
        }
      })()}
      {optionals[data.question_type] ? optionals[data.question_type]() : null}
    </>
  );
};

export default QuestionOption;
